'use client';

import React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/lib/i18n/navigation';
import {
  Compass, ShieldCheck,
  HelpCircle, User, Table, BookOpen
} from 'lucide-react';
import { ZODIAC_METADATA } from '@/lib/config/zodiac';
import { CALCULATOR_ITEMS } from '@/lib/config/calculators';
import { CalculatorFaqAccordion } from './CalculatorFaqAccordion';

export interface HouseTableRow {
  houseNumber: number;
  sanskritName: string;
  englishMeaning: string;
  significations: string;
}

export interface EducationalSubsection {
  title: string;
  icon?: React.ReactNode;
  content: string | React.ReactNode;
}

export interface CalculatorFaqItem {
  q: string;
  a: string;
}

export interface CrossLinkItem {
  title: string;
  href: string;
  description: string;
  categoryBadge?: string;
}

export const HI_BHAVA_ORDINALS: Record<number, string> = {
  1: 'प्रथम',
  2: 'द्वितीय',
  3: 'तृतीय',
  4: 'चतुर्थ',
  5: 'पंचम',
  6: 'षष्ठ',
  7: 'सप्तम',
  8: 'अष्टम',
  9: 'नवम',
  10: 'दशम',
  11: 'एकादश',
  12: 'द्वादश',
};

export const EN_BHAVA_ORDINALS: Record<number, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
  5: '5th',
  6: '6th',
  7: '7th',
  8: '8th',
  9: '9th',
  10: '10th',
  11: '11th',
  12: '12th',
};

export interface CalculatorPageContentProps {
  calculatorName: string;
  introParagraph: string;
  methodologyTitle?: string;
  methodologyContent?: string | React.ReactNode;
  houseTableData?: HouseTableRow[];
  subsections?: EducationalSubsection[];
  faqs?: CalculatorFaqItem[];
  relatedCalculators?: CrossLinkItem[];
  showSignPeriodGrid?: boolean;
}

export function CalculatorPageContent({
  calculatorName,
  introParagraph,
  methodologyTitle,
  methodologyContent,
  houseTableData,
  subsections = [],
  faqs = [],
  relatedCalculators = [],
  showSignPeriodGrid = true
}: CalculatorPageContentProps) {
  const locale = useLocale();
  const tContent = useTranslations('calculatorContent');
  const tZodiac = useTranslations('zodiacSigns');

  const totalCalculators = CALCULATOR_ITEMS.length;
  const resolvedMethodologyTitle = methodologyTitle || tContent('defaultMethodologyTitle');
  const authorName = tContent('authorName');

  return (
    <div className="space-y-16 mt-12 pt-12 border-t border-white/10" data-testid="calculator-page-content">
      {/* 1. OVERVIEW & METHODOLOGY SECTION */}
      <section className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#5ab4ff] block">
              {tContent('principles')}
            </span>
            <h2 className="text-2xl font-heading font-medium text-white flex items-center gap-2.5">
              <Compass className="w-5 h-5 text-[#5ab4ff]" />
              <span>{resolvedMethodologyTitle}</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-[rgba(220,236,255,0.85)] bg-white/[0.05] px-3.5 py-1.5 rounded-full border border-white/15">
            <ShieldCheck className="w-4 h-4 text-[#8dffc9]" />
            <span>{tContent('ephemerisBadge')}</span>
          </div>
        </div>

        <div className="space-y-4 text-xs sm:text-sm text-white leading-[1.6]">
          <p className="bg-white/[0.04] border border-white/12 p-4 rounded-xl text-[rgba(220,236,255,0.95)] font-medium leading-[1.6]">
            {introParagraph}
          </p>

          {methodologyContent && (
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded-xl space-y-2 text-[rgba(220,236,255,0.85)] leading-[1.6]">
              {typeof methodologyContent === 'string' ? <p>{methodologyContent}</p> : methodologyContent}
            </div>
          )}
        </div>
      </section>

      {/* 2. DATA TABLE SECTION (If provided) */}
      {houseTableData && houseTableData.length > 0 && (
        <section className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl space-y-6" data-testid="house-table-section">
          <div className="border-b border-white/10 pb-4 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#5ab4ff] block">
                {tContent('bhavaGuideTitle')}
              </span>
              <h2 className="text-2xl font-heading font-medium text-white flex items-center gap-2.5">
                <Table className="w-5 h-5 text-[#5ab4ff]" />
                <span>{tContent('bhavaGuideSubtitle')}</span>
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs sm:text-sm text-white border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/10 text-white font-heading font-medium">
                  <th className="py-3.5 px-4">{tContent('bhavaColHouse')}</th>
                  <th className="py-3.5 px-4">{tContent('bhavaColSanskrit')}</th>
                  <th className="py-3.5 px-4">{tContent('bhavaColMeaning')}</th>
                  <th className="py-3.5 px-4">{tContent('bhavaColSignifications')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {houseTableData.map((row) => {
                  const ordinal = (locale === 'hi' ? HI_BHAVA_ORDINALS[row.houseNumber] : EN_BHAVA_ORDINALS[row.houseNumber]) || String(row.houseNumber);
                  return (
                    <tr key={row.houseNumber} className="hover:bg-white/[0.04] transition-colors even:bg-white/[0.02]">
                      <td className="py-3.5 px-4 font-mono font-bold text-[#b9c6ff]">
                        {tContent('bhavaHouseFormat', { ordinal })}
                      </td>
                      <td className="py-3.5 px-4 font-semibold text-white">{row.sanskritName}</td>
                      <td className="py-3.5 px-4 text-[rgba(220,236,255,0.85)] font-medium">{row.englishMeaning}</td>
                      <td className="py-3.5 px-4 text-[rgba(220,236,255,0.75)]">{row.significations}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* 3. SITE-WIDE PERSONA SECTION */}
      <section className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl space-y-6" data-testid="raj-persona-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-14 h-14 rounded-full border border-white/20 bg-white/10 flex items-center justify-center shrink-0 shadow-inner" data-testid="raj-persona-avatar">
            <User className="w-7 h-7 text-[#5ab4ff]" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <h2 className="text-2xl font-heading font-semibold text-white" data-testid="raj-persona-name">
                {authorName}
              </h2>
              <span className="text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full bg-[#5ab4ff]/15 text-[#5ab4ff] border border-[#5ab4ff]/30">
                {tContent('rajTitle')}
              </span>
            </div>
            <p className="text-xs text-[rgba(220,236,255,0.75)] font-mono">
              {tContent('rajSpecialty')}
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-[rgba(220,236,255,0.85)] leading-[1.6]">
          {tContent('rajBio', { calculatorName, authorName })}
        </p>

        {/* Supplementary Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center bg-white/[0.04] border border-white/12 p-4 rounded-xl">
          <div>
            <div className="text-xl font-heading font-bold text-[#b9c6ff]">12+ {tContent('years')}</div>
            <div className="text-[11px] text-[rgba(220,236,255,0.75)]">{tContent('yearsStudy')}</div>
          </div>
          <div>
            <div className="text-xl font-heading font-bold text-white">500+</div>
            <div className="text-[11px] text-[rgba(220,236,255,0.75)]">{tContent('guidesAuthored')}</div>
          </div>
          <div>
            <div className="text-xl font-heading font-bold text-[#8dffc9]">100%</div>
            <div className="text-[11px] text-[rgba(220,236,255,0.75)]">{tContent('precisionMetric')}</div>
          </div>
          <div>
            <div className="text-xl font-heading font-bold text-white">{totalCalculators}</div>
            <div className="text-[11px] text-[rgba(220,236,255,0.75)]">{tContent('calculatorsAudited', { count: totalCalculators })}</div>
          </div>
        </div>
      </section>

      {/* 4. EDUCATIONAL SUBSECTIONS */}
      {subsections.length > 0 && (
        <section className="space-y-6" data-testid="educational-subsections">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subsections.map((sub, idx) => (
              <div key={idx} className="rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl space-y-3">
                <div className="p-2.5 rounded-xl bg-white/10 text-[#5ab4ff] w-fit border border-white/15">
                  {sub.icon || <BookOpen className="w-5 h-5 text-[#5ab4ff]" />}
                </div>
                <h3 className="text-lg font-heading font-semibold text-white">{sub.title}</h3>
                <div className="text-xs sm:text-sm text-[rgba(220,236,255,0.85)] leading-[1.6] space-y-2">
                  {typeof sub.content === 'string' ? <p>{sub.content}</p> : sub.content}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5. FAQ ACCORDION SECTION */}
      {faqs.length > 0 && (
        <section className="space-y-6" data-testid="calculator-faq-section">
          <div className="border-b border-white/10 pb-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#5ab4ff] block mb-1">
              {tContent('faqEyebrow')}
            </span>
            <h2 className="text-2xl font-heading font-medium text-white flex items-center gap-2.5">
              <HelpCircle className="w-5 h-5 text-[#5ab4ff]" />
              <span>{tContent('faqHeading')}</span>
            </h2>
          </div>

          <CalculatorFaqAccordion faqs={faqs} />
        </section>
      )}

      {/* 6. CROSS-LINK SECTIONS */}
      <section className="space-y-8 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl" data-testid="calculator-crosslinks-section">
        <div className="border-b border-white/10 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#5ab4ff] block mb-1">
              {tContent('exploreTools')}
            </span>
            <h2 className="text-2xl font-heading font-medium text-white">
              {tContent('relatedHeading')}
            </h2>
          </div>
          <Link
            href="/all-calculators"
            className="px-5 py-2.5 rounded-full bg-white text-[#03163d] font-semibold text-xs shrink-0 hover:bg-white/90 shadow-[0_0_20px_rgba(150,165,255,0.4)] transition-all cursor-pointer"
            data-testid="all-calculators-crosslink"
          >
            {tContent('viewAll', { count: totalCalculators })}
          </Link>
        </div>

        {/* Feature Cards Grid */}
        {relatedCalculators.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedCalculators.map((item, idx) => (
              <div key={idx} className="group rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/12 hover:border-white/25 p-5 space-y-3 transition-all backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-heading font-semibold text-white group-hover:text-[#b9c6ff] transition-colors">{item.title}</span>
                  {item.categoryBadge && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-white/10 text-[#b9c6ff] border border-white/15">
                      {item.categoryBadge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[rgba(220,236,255,0.75)] leading-relaxed">{item.description}</p>
                <Link
                  href={item.href}
                  className="text-xs font-semibold text-[#5ab4ff] group-hover:text-white transition-colors block pt-1"
                >
                  {tContent('openPrefix', { name: item.title })} →
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Optional 12 Signs x 4 Periods Deep-Link Grid */}
        {showSignPeriodGrid && (
          <div className="pt-4 border-t border-white/10 space-y-4">
            <h3 className="text-sm font-heading font-semibold text-white">
              {tContent('browseForecasts')}
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {ZODIAC_METADATA.map((z) => {
                let signLabel = z.name;
                try {
                  signLabel = tZodiac(z.sign.toLowerCase() as any) || z.name;
                } catch (_) {}

                return (
                  <div key={z.sign} className="min-w-0 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 p-3 space-y-2 text-center transition-all">
                    <div className="text-xs font-heading font-semibold text-white truncate">{signLabel}</div>
                    <div className="grid grid-cols-2 gap-1 text-[11px] font-mono">
                      <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=daily`} className="px-1.5 py-1 rounded text-[#5ab4ff] hover:text-white hover:bg-white/10 truncate transition-colors">
                        {tContent('periodDaily')}
                      </Link>
                      <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=weekly`} className="px-1.5 py-1 rounded text-[#5ab4ff] hover:text-white hover:bg-white/10 truncate transition-colors">
                        {tContent('periodWeekly')}
                      </Link>
                      <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=monthly`} className="px-1.5 py-1 rounded text-[#5ab4ff] hover:text-white hover:bg-white/10 truncate transition-colors">
                        {tContent('periodMonthly')}
                      </Link>
                      <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=yearly`} className="px-1.5 py-1 rounded text-[#5ab4ff] hover:text-white hover:bg-white/10 truncate transition-colors">
                        {tContent('periodYearly')}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
